import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { trpc } from "@/lib/trpc";
import { Link, useLocation, useSearch } from "wouter";
import { ArrowLeft, Calendar, Clock, Trash2, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { TenantLogo } from "@/components/TenantLogo";
import { appendEmbedParam } from "@/hooks/useEmbedMode";
import { format } from "date-fns";
import { de } from "date-fns/locale";

function getStatusBadge(status: string) {
  const config: Record<string, { label: string; className: string }> = {
    pending:   { label: "Ausstehend", className: "bg-orange-100 text-orange-700" },
    confirmed: { label: "Bestätigt",  className: "bg-green-100 text-green-700" },
    cancelled: { label: "Storniert",  className: "bg-gray-100 text-gray-700" },
    waitlist:  { label: "Warteliste", className: "bg-blue-100 text-blue-700" },
  };
  const c = config[status] ?? config.pending;
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${c.className}`}>{c.label}</span>;
}

export default function MyCourses() {
  const { user, loading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const search = useSearch();
  const token = new URLSearchParams(search).get("token");

  const isMagicLinkMode = !!token;
  const isOAuthMode = !token && !!user;

  const { data: tokenValidation, isLoading: isValidating } = trpc.selfService.validateToken.useQuery(
    { token: token || "" },
    { enabled: isMagicLinkMode }
  );

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [cancelConfirmId, setCancelConfirmId] = useState<number | null>(null);

  const { data: magicBookings, isLoading: magicLoading } = trpc.selfService.getMyBookings.useQuery(
    { token: token || "" },
    { enabled: isMagicLinkMode && tokenValidation?.valid === true }
  );

  const { data: courseLevelBookings, isLoading: clbLoading, refetch: refetchCLB } = trpc.booking.myCourseLevelBookings.useQuery(
    undefined,
    { enabled: isOAuthMode }
  );

  const { data: sessionBookings, isLoading: sbLoading, refetch: refetchSB } = trpc.sessionBooking.myBookings.useQuery(
    undefined,
    { enabled: isOAuthMode }
  );

  const updateProfileMagic = trpc.selfService.updateProfile.useMutation({
    onSuccess: () => toast.success("Profil aktualisiert"),
    onError: (e) => toast.error("Fehler", { description: e.message }),
  });
  const updateProfileOAuth = trpc.selfService.updateProfileByEmail.useMutation({
    onSuccess: () => toast.success("Profil aktualisiert"),
    onError: (e) => toast.error("Fehler", { description: e.message }),
  });

  const setAutoBookMutation = trpc.booking.setAutoBook.useMutation({
    onSuccess: () => { toast.success("Einstellung gespeichert"); refetchCLB(); },
    onError: (e) => toast.error("Fehler", { description: e.message }),
  });

  const cancelBookingMutation = trpc.sessionBooking.cancel.useMutation({
    onSuccess: () => { toast.success("Buchung storniert"); refetchSB(); },
    onError: (e) => toast.error("Stornierung fehlgeschlagen", { description: e.message }),
  });

  useEffect(() => {
    if (isMagicLinkMode && magicBookings && magicBookings.length > 0) {
      setName(magicBookings[0].participantName || "");
      setPhone(magicBookings[0].participantPhone || "");
      setSmsConsent(!!magicBookings[0].smsConsent);
    } else if (isOAuthMode && user) {
      setName(user.name || "");
    }
  }, [magicBookings, user, isMagicLinkMode, isOAuthMode]);

  useEffect(() => {
    if (!authLoading && !user && !token) {
      setLocation("/self-service/login");
    }
  }, [authLoading, user, token, setLocation]);

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isMagicLinkMode) {
      updateProfileMagic.mutate({ token: token!, name, phone, smsConsent });
    } else if (user?.email) {
      updateProfileOAuth.mutate({ email: user.email, name, phone, smsConsent });
    }
  };

  const today = new Date().toISOString().split("T")[0];
  const upcomingSessionBookings = sessionBookings?.filter(b => b.session.sessionDate >= today) || [];
  const pastSessionBookings = sessionBookings?.filter(b => b.session.sessionDate < today) || [];

  if (authLoading || (isMagicLinkMode && isValidating)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isMagicLinkMode && !tokenValidation?.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Ungültiger Link</CardTitle>
            <CardDescription>{tokenValidation?.error || "Der Link ist ungültig oder abgelaufen."}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => setLocation("/self-service/login")} className="w-full">
              Neuen Link anfordern
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isDataLoading = clbLoading || sbLoading || magicLoading;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container py-8 max-w-4xl">
        <Link href={appendEmbedParam("/")}>
          <Button variant="ghost" className="mb-6 gap-2 -ml-2">
            <ArrowLeft className="h-4 w-4" />
            Zur Website
          </Button>
        </Link>

        <div className="mb-8">
          <h1 className="text-3xl font-bold">Mein Bereich</h1>
          <p className="mt-1 text-muted-foreground">Ihre Anmeldungen, Termine und Profil</p>
        </div>

        {isDataLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <Tabs defaultValue={isOAuthMode ? "termine" : "registrations"} className="space-y-6">
            <TabsList>
              {isOAuthMode && (
                <TabsTrigger value="termine">
                  Termine
                  {upcomingSessionBookings.length > 0 && (
                    <Badge variant="secondary" className="ml-2">{upcomingSessionBookings.length}</Badge>
                  )}
                </TabsTrigger>
              )}
              <TabsTrigger value="registrations">
                Anmeldungen
                {(isOAuthMode ? (courseLevelBookings?.length ?? 0) : (magicBookings?.length ?? 0)) > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {isOAuthMode ? courseLevelBookings?.length : magicBookings?.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="profil">Profil</TabsTrigger>
            </TabsList>

            {/* ===== TAB: ANMELDUNGEN ===== */}
            <TabsContent value="registrations" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Meine Kursanmeldungen</CardTitle>
                  <CardDescription>Kursanmeldungen und deren Status</CardDescription>
                </CardHeader>
                <CardContent>
                  {isOAuthMode && (
                    <>
                      {!courseLevelBookings || courseLevelBookings.length === 0 ? (
                        <div className="text-center py-8">
                          <Calendar className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                          <p className="text-muted-foreground">Keine Anmeldungen vorhanden.</p>
                          <Link href={appendEmbedParam("/")}><Button variant="outline" className="mt-4">Kurse entdecken</Button></Link>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {courseLevelBookings.map((booking) => (
                            <div key={booking.id} className="border rounded-lg p-4 space-y-2">
                              <div className="flex items-start justify-between">
                                <div>
                                  <Link href={appendEmbedParam(`/course/${booking.courseId}`)}>
                                    <h3 className="font-semibold hover:text-primary cursor-pointer">{booking.course.name}</h3>
                                  </Link>
                                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
                                    <Clock className="h-3 w-3" />
                                    {booking.course.startTime} – {booking.course.endTime} Uhr
                                  </p>
                                </div>
                                {getStatusBadge(booking.status)}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Angemeldet am {format(new Date(booking.createdAt), "dd.MM.yyyy", { locale: de })}
                              </p>
                              {booking.status === "confirmed" && !booking.course.isBlockCourse && (
                                <div className="flex items-center gap-3 mt-2">
                                  <button
                                    role="switch"
                                    aria-checked={!!booking.autoBook}
                                    onClick={() => setAutoBookMutation.mutate({ bookingId: booking.id, autoBook: !booking.autoBook })}
                                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${booking.autoBook ? "bg-primary" : "bg-muted-foreground/30"}`}
                                  >
                                    <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-lg transition-transform ${booking.autoBook ? "translate-x-4" : "translate-x-0"}`} />
                                  </button>
                                  <span className="text-xs text-muted-foreground">Neue Termine automatisch buchen</span>
                                </div>
                              )}
                              {booking.status === "confirmed" && !booking.course.isBlockCourse && (
                                <Link href={appendEmbedParam(`/course/${booking.courseId}`)}>
                                  <Button size="sm" variant="outline" className="mt-1">
                                    <Calendar className="h-3.5 w-3.5 mr-1.5" />
                                    Termin buchen
                                  </Button>
                                </Link>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {isMagicLinkMode && (
                    <>
                      {!magicBookings || magicBookings.length === 0 ? (
                        <div className="text-center py-8">
                          <Calendar className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                          <p className="text-muted-foreground">Keine Anmeldungen gefunden.</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {magicBookings.map((booking) => (
                            <div key={booking.id} className="border rounded-lg p-4 space-y-2">
                              <div className="flex items-start justify-between">
                                <div>
                                  <h3 className="font-semibold">{booking.course?.name}</h3>
                                  <p className="text-sm text-muted-foreground">
                                    {booking.course?.startTime} – {booking.course?.endTime} Uhr
                                  </p>
                                </div>
                                {getStatusBadge(booking.status)}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Angemeldet am {format(new Date(booking.createdAt), "dd.MM.yyyy", { locale: de })}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ===== TAB: TERMINE (OAuth only) ===== */}
            {isOAuthMode && (
              <TabsContent value="termine" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Meine Termine</CardTitle>
                    <CardDescription>Gebuchte Einzel-Sessions</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {upcomingSessionBookings.length === 0 ? (
                      <div className="text-center py-8">
                        <Calendar className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">Keine kommenden Termine.</p>
                        <Link href={appendEmbedParam("/calendar")}>
                          <Button variant="outline" className="mt-4">Zum Kurskalender</Button>
                        </Link>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {upcomingSessionBookings.map((booking) => (
                          <div key={booking.id} className="flex items-center justify-between p-4 rounded-lg border">
                            <div>
                              <p className="font-medium">{booking.course.name}</p>
                              <p className="text-sm text-muted-foreground flex items-center gap-2 mt-0.5">
                                <span>{format(new Date(booking.session.sessionDate + 'T12:00:00'), "EEE, dd.MM.yyyy", { locale: de })}</span>
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />{booking.course.startTime}–{booking.course.endTime}
                                </span>
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              {booking.session.isCancelled
                                ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Abgesagt</span>
                                : getStatusBadge(booking.status)}
                              {!booking.session.isCancelled && booking.status !== "cancelled" && (
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={() => setCancelConfirmId(booking.id)}
                                  disabled={cancelBookingMutation.isPending}
                                >
                                  {cancelBookingMutation.isPending && cancelConfirmId === booking.id
                                    ? <Loader2 className="h-4 w-4 animate-spin" />
                                    : <Trash2 className="h-4 w-4 text-destructive" />}
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {pastSessionBookings.length > 0 && (
                      <div className="mt-6">
                        <p className="text-sm font-medium text-muted-foreground mb-3">Vergangene Termine</p>
                        <div className="space-y-2">
                          {pastSessionBookings.slice(0, 5).map((booking) => (
                            <div key={booking.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 text-sm">
                              <div>
                                <span className="font-medium">{booking.course.name}</span>
                                <span className="text-muted-foreground ml-2">
                                  {new Date(booking.session.sessionDate + 'T12:00:00').toLocaleDateString("de-DE")}
                                </span>
                              </div>
                              {booking.session.isCancelled
                                ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Abgesagt</span>
                                : getStatusBadge(booking.status)}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            {/* ===== TAB: PROFIL ===== */}
            <TabsContent value="profil">
              <Card>
                <CardHeader>
                  <CardTitle>Mein Profil</CardTitle>
                  <CardDescription>Persönliche Daten und Benachrichtigungseinstellungen</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleProfileSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="profile-name">Name</Label>
                      <Input id="profile-name" value={name} onChange={(e) => setName(e.target.value)} required />
                    </div>
                    {isOAuthMode && (
                      <div className="space-y-2">
                        <Label>E-Mail</Label>
                        <Input value={user?.email || ""} disabled className="bg-muted" />
                        <p className="text-xs text-muted-foreground">E-Mail kann nicht geändert werden</p>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="profile-phone">Telefon</Label>
                      <Input id="profile-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+49 ..." />
                    </div>
                    <Separator />
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="smsConsent"
                        checked={smsConsent}
                        onCheckedChange={(v) => setSmsConsent(v === true)}
                      />
                      <div>
                        <Label htmlFor="smsConsent" className="cursor-pointer">SMS-Benachrichtigungen</Label>
                        <p className="text-sm text-muted-foreground">Kursänderungen und Erinnerungen per SMS erhalten</p>
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={updateProfileMagic.isPending || updateProfileOAuth.isPending}
                    >
                      {(updateProfileMagic.isPending || updateProfileOAuth.isPending)
                        ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Wird gespeichert...</>
                        : "Änderungen speichern"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </main>

      {/* Stornieren Confirm Dialog */}
      {cancelConfirmId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-sm mx-4">
            <CardHeader>
              <CardTitle>Termin stornieren</CardTitle>
              <CardDescription>Möchten Sie diesen Termin wirklich stornieren? Diese Aktion kann nicht rückgängig gemacht werden.</CardDescription>
            </CardHeader>
            <CardContent className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setCancelConfirmId(null)}>Abbrechen</Button>
              <Button
                variant="destructive"
                disabled={cancelBookingMutation.isPending}
                onClick={() => {
                  cancelBookingMutation.mutate({ bookingId: cancelConfirmId! });
                  setCancelConfirmId(null);
                }}
              >
                Stornieren
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function Header() {
  const { user } = useAuth();

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between">
        <Link href="/" className="flex items-center gap-3">
          <TenantLogo className="h-12 w-auto" />
        </Link>
        <nav className="flex items-center gap-4">
          {user && (
            <span className="text-sm text-muted-foreground">
              {user.name || user.email}
            </span>
          )}
        </nav>
      </div>
    </header>
  );
}
